import React, { useState, useEffect, useMemo } from "react";
import { Dropdown, Row, Col, Button, Form, Toast } from "react-bootstrap";
import { useFormik } from "formik";
import { ActionHandlerFunction, AdminApprovalGroup, AdminUser, ApproversInfo } from "@preveil-api";
import {
  Account, AddUsersMessaging, AdminErrorMessages, AdminSuccessMessages, AdminUIActionTypes, AdminWarningMessages, isSameUser, Message, MessageHandlerDisplayType, MessageToastTypes,
  OrgUserRoles, read_only_states, RegexHelper, useAddOrgApprovalGroupMembersMutation, useAppDispatch, useAppSelector, useUpdateUserOrgDeptMutation, useUpdateUserOrgRoleMutation, usePutUsersDisplayNameMutation, MessageAnchors,
  account_types
} from "src/common";
import { InfoPopover } from "src/components";
import { GroupMembersTable } from "..";
import { uiActions } from "src/store";
import { RootState } from "src/store/configureStore";
import _ from "lodash";
import * as Yup from "yup";

type AllProps = {
  current_account: Account;
  total_admin_users: number;
  current_selected_user: AdminUser;
  recovery_groups: AdminApprovalGroup[];
  handleAction: ActionHandlerFunction;
  users_list: AdminUser[];
  admin_approval_group?: AdminApprovalGroup;
};

function AccountPanelComponent(props: AllProps) {
  const { total_admin_users, current_selected_user, recovery_groups, handleAction, current_account, users_list, admin_approval_group } = props;
  const is_account_express = current_selected_user.account_type === account_types.express;
  const dispatch = useAppDispatch();
  const org_info = useAppSelector((state: RootState) => state.account.organization);
  const { display_name, user_id, role, status, department, recovery_group, pending_recovery_group_id } = current_selected_user;
  const is_same_group_id = pending_recovery_group_id === current_selected_user.recovery_group?.approval_group_id;
  const [selected_recovery_group, setSelectedRecoveryGroup] = useState<AdminApprovalGroup | undefined>(current_selected_user.recovery_group);
  const [is_pending_shards, setIsPendingShards] = useState<boolean>(false);
  const [assigned_role, setAssignedRole] = useState<string>("");
  const [enable_save, setEnableSave] = useState<boolean>(false);
  const department_invalid_message = "The department is required";
  const name_invalid_message = "The name is required";
  const is_read_only = read_only_states[role] || read_only_states[status];
  // If there is only one admin user in the org and the current user is an admin, disable the role change.
  const user_part_of_admin_management = (admin_approval_group?.approvers.find((approver) => approver.address === current_selected_user.user_id));
  const role_message = "This user is part of the admin management group therefore their role cannot be changed. To change their role, please remove them from the admin management group.";
  const disable_role_change = (total_admin_users === 1 && current_selected_user.role === OrgUserRoles.admin) || !!user_part_of_admin_management;
  const disabled = is_read_only && role.toLowerCase() !== OrgUserRoles.admin;
  const user_role = _.capitalize(OrgUserRoles[assigned_role]) || "";
  const [addOrgApprovalGroupMembers] = useAddOrgApprovalGroupMembersMutation();
  const [updateUserOrgRole] = useUpdateUserOrgRoleMutation();
  const [updateUserOrgDept] = useUpdateUserOrgDeptMutation();
  const [updateUserDisplayName] = usePutUsersDisplayNameMutation();
  // Formik setup for department and name fields, for easier validation.
  const formik = useFormik({
    validationSchema: Yup.object().shape({
      department_field: Yup.string().required(department_invalid_message),
      name_field: Yup.string()
        .required(name_invalid_message)
        .test("name-field-chars", AddUsersMessaging.invalid_display_name_chars, (value) => {
          return !value || RegexHelper.testDisplayNameChars(value);
        })
        .test("name-field-length", AddUsersMessaging.invalid_display_name_length, (value) => {
          return !value || value.length < 256;
        })
    }),
    initialValues: {
      department_field: department,
      name_field: display_name,
    },
    enableReinitialize: true,
    onSubmit: () => { }
  });

  // Description: if the user is already part of an approval group, we need to show a warning message
  // if the user will be added to a different approval group, that doing so will invalidate their current recovery file.
  const warning_msg_recovery_group = useMemo(() => {
    if (!!recovery_group && !!selected_recovery_group && recovery_group.name !== selected_recovery_group.name) {
      return AdminWarningMessages.user_change_recovery_group;
    }
    return null;
  }, [recovery_group, selected_recovery_group]);

  useEffect(() => {
    setAssignedRole(role);
    if (!!pending_recovery_group_id) {
      // NOTE: We need to run postlord to get the notifications updates for current_selected_user.recovery_group_pending, otherwise
      // it will always be true after the first group change.
      // If pending_recovery_group_id is not the same as the current user's recovery group approval group id, that means the user still needs to submit their shards (open PreVeil).
      const is_pending_group_change = pending_recovery_group_id !== current_selected_user.recovery_group?.approval_group_id;
      // If the user is assigned a recovery group for the first time we need to check if the recovery_group_pending is true.
      const is_pending_group_assigned = pending_recovery_group_id === current_selected_user.recovery_group?.approval_group_id && !!current_selected_user.recovery_group_pending;
      setIsPendingShards(is_pending_group_change || is_pending_group_assigned);
    }
  }, [current_selected_user, pending_recovery_group_id]);

  useEffect(() => {
    const name_field_changed = formik.values.name_field !== display_name;
    const department_field_changed = formik.dirty && !!formik.values.department_field;
    const role_changed = assigned_role !== current_selected_user?.role;
    const recovery_group_changed = selected_recovery_group?.name !== current_selected_user.recovery_group?.name;
    if (name_field_changed || department_field_changed || role_changed || recovery_group_changed) {
      setEnableSave(true);
    } else if (enable_save) {
      setEnableSave(false);
    }
  }, [formik.values.name_field, formik.values.department_field, assigned_role, selected_recovery_group, display_name]);

  // Description: handle dropdown change for user role.
  function handleUserRoleDropdownChange(event: React.MouseEvent<HTMLElement>) {
    const { id } = event.target as HTMLElement;
    setAssignedRole(OrgUserRoles[id]);
  }

  // Description: handle dropdown change for recovery group.
  function handleDropdownChange(event: React.MouseEvent<HTMLElement>) {
    const { id } = event.target as HTMLElement;
    const selected_group = recovery_groups.filter((group) => group.approval_group_id === id);
    if (selected_group.length === 1) {
      setSelectedRecoveryGroup(selected_group[0]);
    }
  }

  // Description: handles submit for updating user role, department and recovery group.
  // if any or all of the fields are changed, it will send back an object with the updated fields params.
  // the field will be set to null inside the object (updatedUser) if it's not changed.
  function handleSubmit() {
    const { name_field, department_field } = formik.values;
    const name_changed = name_field !== display_name;
    const recovery_group_changed = current_selected_user.recovery_group?.name !== selected_recovery_group?.name;
    const role_changed = assigned_role !== current_selected_user.role;
    const department_changed = department_field !== department;
    if (name_changed || recovery_group_changed || role_changed || department_changed) {
      updateProfile(name_changed ? name_field : undefined, role_changed ? assigned_role : undefined, department_changed ? department_field : undefined, recovery_group_changed ? selected_recovery_group : undefined);
      setEnableSave(false);
    }
  }

  // Description: This function will handle the update of the user's profile, including their name, role, department and recovery group. 
  // It will only send the fields that have been changed to the API. 
  async function updateProfile(name?: string, role?: string, department?: string, recovery_group?: AdminApprovalGroup) {
    if (!!org_info) {
      const stack = [];
      const current_user_id = current_account.user_id;
      const org_id = org_info.org_id;
      const member_id = current_selected_user.user_id;
      const user_address = current_selected_user.user_id;

      try {
        !!role && stack.push(updateUserOrgRole({ orgId: org_id, userId: current_user_id, memberId: member_id, role, userAddress: user_address }));
        !!department && stack.push(updateUserOrgDept({ orgId: org_id, userId: current_user_id, memberId: member_id, department }));
        !!name && stack.push(updateUserDisplayName({ account_ids: Account.getAccountIdentifiers(current_account), body: { user_id: current_selected_user.user_id, display_name: name } }));
        if (!!recovery_group) {
          const current_group_version = current_selected_user.recovery_group?.group_version || null;
          const current_group_id = current_selected_user.recovery_group?.approval_group_id || null;
          const approvers: ApproversInfo[] = [];
          let is_approvers_error = false;
          for (const approver of recovery_group.approvers) {
            const user = users_list.find((user) => isSameUser(user.user_id, approver.address));
            // Note: We have to make sure we have the correct account version to ensure the recovery process works correctly.
            // If the approver is not in the users list, we can not know their account version, so we need to show an error message and stop the recovery group update.
            if (!user) {
              const message = AdminErrorMessages.error_recovery_group_approver_not_found.replace(MessageAnchors.user_id, approver.address);
              dispatch(uiActions.handleSetMessage(new Message(message, MessageHandlerDisplayType.toastr, MessageToastTypes.danger)));
              is_approvers_error = true;
              break;
            }
            approvers.push({ user_id: approver.address, account_version: user.account_version, required: false });
          }
          if (!is_approvers_error) {
            stack.push(addOrgApprovalGroupMembers({
              orgId: org_id, userId: current_user_id, groupVersion: recovery_group.group_version, approvalGroupUid: recovery_group.approval_group_id, currentGroupId: current_group_id,
              currentGroupVersion: current_group_version, approvers, optionalsRequired: recovery_group.required_approvers, users: [current_selected_user.user_id]
            }));
          }
        }
        if (stack.length >= 1) {
          const results = await Promise.allSettled(stack);
          let has_error = false;
          // Handle each result individually
          results.forEach((result) => {
            // RTK Query mutations resolve with { error } on failure rather than rejecting,
            // so check both the rejected case and the fulfilled-with-error case.
            if (result.status === "rejected" || (result.status === "fulfilled" && "error" in result.value)) {
              has_error = true;
              const message = AdminErrorMessages.error_update_user_account.replace(MessageAnchors.user_id, `${current_selected_user.display_name}`);
              const error = result.status === "rejected" ? result.reason : result.value.error;
              dispatch(uiActions.handleRequestErrors(new Message(message, MessageHandlerDisplayType.toastr), error));
            }
          });
          if (!has_error) {
            let message: string;
            let display_type = MessageToastTypes.warning;
            if (admin_approval_group && !!current_selected_user.recovery_group && !!role && !!recovery_group) {
              message = AdminWarningMessages.warning_approval_multiple_updates.replace(MessageAnchors.user_id, `${current_selected_user.display_name}`);
            } else if (admin_approval_group && !!role) {
              message = AdminWarningMessages.warning_approval_role_update.replace(MessageAnchors.user_id, `${current_selected_user.display_name}`);
            } else if (!!current_selected_user.recovery_group && !!recovery_group) {
              message = AdminWarningMessages.warning_approval_recovery_group_update.replace(MessageAnchors.user_id, `${current_selected_user.display_name}`).replace(MessageAnchors.message_content, current_selected_user.recovery_group.name);
            } else {
              message = AdminSuccessMessages.success_update_user_account.replace(MessageAnchors.user_id, `${current_selected_user.display_name}`);
              display_type = MessageToastTypes.success;
            }
            dispatch(uiActions.handleSetMessage(new Message(message, MessageHandlerDisplayType.toastr, display_type)));
            handleHideModal();
            handleAction({ actionType: AdminUIActionTypes.GetRefreshRequest });
            // if admin is demoting themselves to standard, we need to refresh the page so the correct rules are applied and user is redirected to mail page.
            // otherwise just refresh the org data.
            isSameUser(current_account.user_id, current_selected_user.user_id) && role === OrgUserRoles.standard ? window.location.reload() : handleAction({ actionType: AdminUIActionTypes.GetRefreshRequest });
          }
        }
      } catch (error) {
        handleHideModal();
        const message = AdminErrorMessages.error_update_user_account.replace(MessageAnchors.user_id, `${current_selected_user.display_name}`);
        dispatch(uiActions.handleRequestErrors(new Message(message, MessageHandlerDisplayType.toastr), error));
      }
    }
  }

  // Note: Reset fields to original values and hide modal.
  function handleHideModal() {
    setSelectedRecoveryGroup(current_selected_user.recovery_group);
    setAssignedRole(current_selected_user.role);
    formik.dirty && formik.resetForm();
    handleAction({ actionType: AdminUIActionTypes.ShowSideNavMenu });
  }

  function findRecoveryGroupById(id: string | null | undefined): string | undefined {
    if (!!id) {
      return recovery_groups.find((group) => group.approval_group_id === id)?.name;
    }
  }

  // NOTE: Reverted the Name form and functionality. To bring it back, see:
  // https://github.com/PreVeil/web-v2/blob/43b14043a3c247a546a54a2566ae5bd30854cd58/src/components/admin/admin-users/side-panel/AccountPanel.component.tsx
  return <>
    <Form className="user-form">
      <Form.Group as={Row}>
        <Col sm={2}>
          <Form.Label className="h5">Name</Form.Label>
        </Col>
        <Col sm={10}>
          <Form.Control
            type="text"
            name="name-field"
            placeholder="Name"
            disabled={disabled}
            onChange={formik.handleChange("name_field")}
            onBlur={formik.handleBlur("name_field")}
            value={formik.values.name_field}
            isInvalid={formik.touched.name_field && !!formik.errors.name_field}
          />
          {
            formik.touched.name_field && !!formik.errors.name_field &&
            <Form.Text className="invalid-feedback">
              {formik.errors.name_field}
            </Form.Text>
          }
        </Col>
      </Form.Group>
    </Form>
    <div className="user-table mt-3">
      <p>
        <span>Email Address</span> {user_id}
      </p>
      <p className="text-capitalize">
        <span>Status</span> {status}
      </p>
      {(disabled || disable_role_change || is_account_express) &&
        <p className="text-capitalize">
          <span>Role {!!user_part_of_admin_management && <InfoPopover message={role_message} />}</span> {role}
        </p>
      }
    </div>
    {(!disabled && !disable_role_change && !is_account_express) &&
      <Row className="mb-3">
        <Col sm={2}>
          <Form.Label className="h5">Role</Form.Label>
        </Col>
        <Col sm={10}>
          <Dropdown className="pv-dropdown">
            <Dropdown.Toggle variant="outline-secondary">
              {!!role ? `${user_role} User` : "Select User Role"}
            </Dropdown.Toggle>
            <Dropdown.Menu align="end">
              <Dropdown.Item id="standard" onClick={handleUserRoleDropdownChange}>Standard User</Dropdown.Item>
              <Dropdown.Item id="admin" onClick={handleUserRoleDropdownChange}>Admin User</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </Col>
      </Row>}
    <Form className="user-form">
      <Form.Group as={Row}>
        <Col sm={2}>
          <Form.Label className="h5">Department</Form.Label>
        </Col>
        <Col sm={10}>
          <Form.Control
            type="text"
            name="department-field"
            placeholder="Department"
            disabled={disabled}
            onChange={formik.handleChange("department_field")}
            onBlur={formik.handleBlur("department_field")}
            value={formik.values.department_field}
            isInvalid={formik.touched.department_field && !!formik.errors.department_field}
          />
          {
            formik.touched.department_field && !!formik.errors.department_field &&
            <Form.Text className="invalid-feedback">
              {department_invalid_message}
            </Form.Text>
          }
        </Col>
      </Form.Group>
    </Form>
    <div className="recovery-group-section">
      <div className="recovery-group-dropdown">
        <h5>Recovery Group</h5>
        {recovery_groups.length > 0 ? <Dropdown className="pv-dropdown">
          <Dropdown.Toggle variant="outline-secondary" disabled={disabled}>
            {!!selected_recovery_group ? `${selected_recovery_group.name}` : "Select Recovery Group"}
          </Dropdown.Toggle>
          <Dropdown.Menu align="end">
            {recovery_groups.map((recovery_group: AdminApprovalGroup) => {
              const { approval_group_id } = recovery_group;
              return (
                <Dropdown.Item key={approval_group_id} id={approval_group_id} onClick={handleDropdownChange}>
                  {recovery_group.name}
                </Dropdown.Item>
              );
            })}
          </Dropdown.Menu>
        </Dropdown> : <span className="no-recovery-group">No recovery groups have been created</span>}
      </div>
      {!!is_pending_shards && (
        <div className="user-table">
          {!is_same_group_id && (
            <p>
              <span>Current Group</span> {findRecoveryGroupById(current_selected_user?.recovery_group?.approval_group_id)}
            </p>
          )}
          <p>
            <span>Pending Group</span> <span>{findRecoveryGroupById(pending_recovery_group_id)} <br /> <i>This user needs to open PreVeil to be assigned this group.</i></span>
          </p>
        </div>
      )}
      {!!selected_recovery_group && selected_recovery_group.approvers.length >= 1 &&
        <GroupMembersTable recovery_group={selected_recovery_group} />
      }
      {
        !!warning_msg_recovery_group &&
        <Toast bg="warning" show={true} className="mt-0">
          <Toast.Body>
            {warning_msg_recovery_group}
          </Toast.Body>
        </Toast>
      }
    </div>
    <div className="btn-panel pt-1 mt-2">
      <Button variant="primary" disabled={!enable_save || !formik.isValid} onClick={handleSubmit}>Save Changes</Button>
      <Button variant="no-outline-primary" onClick={handleHideModal}>Cancel</Button>
    </div>
  </>;
}

export default React.memo(AccountPanelComponent);